import { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatIDR, formatShortIDR } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Download, FileSpreadsheet, TrendingUp } from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

const download = (report) => {
  const url = `${API_BASE}/export/xlsx?report=${report}`;
  window.open(url, "_blank");
};

export default function Reports() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [stock, setStock] = useState(null);
  const [pl, setPl] = useState(null);
  const [sales, setSales] = useState(null);

  useEffect(() => {
    api.get("/reports/stock").then((r) => setStock(r.data));
    api.get("/reports/profit-loss?months=6").then((r) => setPl(r.data));
    api.get("/reports/sales?days=30").then((r) => setSales(r.data));
  }, []);

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-zinc-900">Laporan</h1>
          <p className="text-sm text-zinc-500 mt-1">Lihat performa stok, penjualan, dan laba rugi.</p>
        </div>
        {isOwner && (
          <Button
            data-testid="btn-export-all"
            onClick={() => download("all")}
            variant="outline"
            className="rounded-md"
          >
            <FileSpreadsheet className="h-4 w-4 mr-1.5" />
            Export Semua ke Excel
          </Button>
        )}
      </div>

      <Tabs defaultValue="profit" className="w-full">
        <TabsList className="rounded-md">
          <TabsTrigger value="profit" data-testid="tab-profit">Laba Rugi</TabsTrigger>
          <TabsTrigger value="sales" data-testid="tab-sales">Penjualan</TabsTrigger>
          <TabsTrigger value="stock" data-testid="tab-stock">Stok</TabsTrigger>
        </TabsList>

        <TabsContent value="profit" className="mt-4 space-y-4">
          {pl && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <SummaryCard label="Total Penjualan" value={formatIDR(pl.totals.revenue)} tone="green" />
                <SummaryCard label="Total HPP" value={formatIDR(pl.totals.hpp)} tone="zinc" />
                <SummaryCard label="Total Pembelian" value={formatIDR(pl.totals.purchases)} tone="zinc" />
                <SummaryCard label="Total Laba" value={formatIDR(pl.totals.profit)} tone={pl.totals.profit >= 0 ? "green" : "red"} />
              </div>
              <Card className="p-5 border-zinc-200 shadow-none rounded-md">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-zinc-500" />
                    <h2 className="text-base font-display font-semibold">Grafik Laba Rugi (6 bulan)</h2>
                  </div>
                  {isOwner && (
                    <Button
                      data-testid="btn-export-profit"
                      size="sm"
                      variant="outline"
                      onClick={() => download("profit")}
                      className="rounded-md"
                    >
                      <Download className="h-3.5 w-3.5 mr-1.5" />
                      Excel
                    </Button>
                  )}
                </div>
                <div className="h-72 min-h-[280px]" data-testid="chart-pl">
                  <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                    <BarChart data={pl.months}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#71717a" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#71717a" }} tickFormatter={formatShortIDR} />
                      <Tooltip formatter={(v) => formatIDR(v)} contentStyle={{ borderRadius: 6, fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="revenue" name="Penjualan" fill="#10b981" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="hpp" name="HPP" fill="#a1a1aa" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="profit" name="Laba" fill="#18181b" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
              <Card className="border-zinc-200 shadow-none rounded-md overflow-x-auto">
                <Table>
                  <TableHeader className="bg-zinc-50">
                    <TableRow>
                      <TableHead>Bulan</TableHead>
                      <TableHead className="text-right">Penjualan</TableHead>
                      <TableHead className="text-right">HPP</TableHead>
                      <TableHead className="text-right">Pembelian</TableHead>
                      <TableHead className="text-right">Laba</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pl.months.map((m) => (
                      <TableRow key={m.month}>
                        <TableCell className="font-medium">{m.month}</TableCell>
                        <TableCell className="text-right num text-emerald-700">{formatIDR(m.revenue)}</TableCell>
                        <TableCell className="text-right num text-zinc-600">{formatIDR(m.hpp)}</TableCell>
                        <TableCell className="text-right num text-zinc-600">{formatIDR(m.purchases)}</TableCell>
                        <TableCell className={`text-right num font-semibold ${m.profit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                          {formatIDR(m.profit)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="sales" className="mt-4 space-y-4">
          {sales && (
            <Card className="p-5 border-zinc-200 shadow-none rounded-md">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-display font-semibold">Penjualan & Pembelian 30 Hari Terakhir</h2>
                <div className="flex gap-2">
                  {isOwner && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => download("sales")} data-testid="btn-export-sales" className="rounded-md">
                        <Download className="h-3.5 w-3.5 mr-1.5" />
                        Penjualan
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => download("purchases")} data-testid="btn-export-purchases" className="rounded-md">
                        <Download className="h-3.5 w-3.5 mr-1.5" />
                        Pembelian
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <div className="h-72 min-h-[280px]" data-testid="chart-sales">
                <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                  <LineChart data={sales.daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#71717a" }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 11, fill: "#71717a" }} tickFormatter={formatShortIDR} />
                    <Tooltip formatter={(v) => formatIDR(v)} contentStyle={{ borderRadius: 6, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="out" name="Penjualan" stroke="#10b981" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="in" name="Pembelian" stroke="#f43f5e" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="stock" className="mt-4 space-y-4">
          {stock && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <SummaryCard label="Total Nilai Persediaan" value={formatIDR(stock.total_value)} tone="zinc" />
                <SummaryCard label="Jumlah SKU" value={String(stock.items.length)} tone="zinc" />
              </div>
              <Card className="border-zinc-200 shadow-none rounded-md">
                <div className="p-4 border-b border-zinc-200 flex items-center justify-between">
                  <div className="text-sm font-semibold">Posisi Stok Saat Ini</div>
                  {isOwner && (
                    <Button
                      data-testid="btn-export-stock"
                      size="sm"
                      variant="outline"
                      onClick={() => download("stock")}
                      className="rounded-md"
                    >
                      <Download className="h-3.5 w-3.5 mr-1.5" />
                      Excel
                    </Button>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader className="bg-zinc-50">
                      <TableRow>
                        <TableHead className="w-[120px]">Part Number</TableHead>
                        <TableHead>Nama</TableHead>
                        <TableHead className="hidden sm:table-cell">Kategori</TableHead>
                        <TableHead className="text-right">Gudang</TableHead>
                        <TableHead className="text-right">Toko</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right hidden md:table-cell">Hrg Beli</TableHead>
                        <TableHead className="text-right">Nilai</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {stock.items.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell className="font-mono text-xs text-zinc-600">{p.code}</TableCell>
                          <TableCell className="font-medium">{p.name}</TableCell>
                          <TableCell className="hidden sm:table-cell text-zinc-600">{p.category}</TableCell>
                          <TableCell className="text-right num text-zinc-700">{p.stock_gudang ?? 0}</TableCell>
                          <TableCell className="text-right num text-zinc-700">{p.stock_toko ?? 0}</TableCell>
                          <TableCell className="text-right num font-semibold">
                            {p.current_stock} <span className="text-zinc-400 font-normal">{p.unit}</span>
                          </TableCell>
                          <TableCell className="text-right num hidden md:table-cell text-zinc-600">
                            {formatIDR(p.purchase_price)}
                          </TableCell>
                          <TableCell className="text-right num font-semibold">{formatIDR(p.stock_value)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

const SummaryCard = ({ label, value, tone = "zinc" }) => {
  const toneMap = {
    zinc: "text-zinc-900",
    green: "text-emerald-700",
    red: "text-rose-700",
  };
  return (
    <Card className="p-4 border-zinc-200 shadow-none rounded-md">
      <div className="text-xs uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`mt-1 text-xl font-display font-bold num ${toneMap[tone]}`}>{value}</div>
    </Card>
  );
};
